import {
  postJsMessage,
  unregisterSpecificNotificationHandler,
  registerSpecificNotificationHandler,
} from '../../bridge';
import API from '../../api';
import { Platform } from '../../utils/platform';
import { isAvailable } from '../utils';
import { NativeAPIResponseMessage } from '../../utils/type';
import { PostMessageOption, NotificationHandler } from '../../types';

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.closeWebview
 *
 * [PLATFORM]
 * mac        NotSupport
 * windows    NotSupport
 * linux      NotSupport
 * ios        5.17.0+
 * android    5.17.0+
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export function closeWebview(clearCache: boolean): Promise<any> {
  return isAvailable('webview.closeWebview').then(() => {
    return postJsMessage(API.closeWebview, { clearCache: !!clearCache });
  });
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.getWebviewId
 *
 * [PLATFORM]
 * mac        6.4.0
 * windows    6.4.0
 * linux      6.4.0
 * ios        6.4.0
 * android    6.4.0
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export function getWebviewId(): Promise<
  NativeAPIResponseMessage<{ webviewId: string }>
> {
  return isAvailable('webview.getWebviewId').then(() => {
    return postJsMessage(API.getWebviewId, {});
  });
}

export function isInWebview(): boolean {
  return !!(
    Platform.isAndroid() ||
    Platform.isIOS() ||
    Platform.isWindows() ||
    Platform.isMacOS() ||
    Platform.isLinux()
  );
}

const visibilityHandlerMap = new Map();
/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.onVisibilityChange
 *
 * [PLATFORM]
 * mac        6.0.5+
 * windows    6.0.5+
 * linux      6.0.5+
 * ios        6.0.5+
 * android    6.0.5+
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export function onVisibilityChange(
  visibilityChangeCallback: (visibilityState: VisibilityState) => void
) {
  if (
    Platform.isMacOS() ||
    Platform.isWindows() ||
    Platform.isIOS() ||
    Platform.isAndroid()
  ) {
    const wrapperHandler = () => {
      visibilityChangeCallback(document.visibilityState);
    };
    visibilityHandlerMap.set(visibilityChangeCallback, wrapperHandler);
    document.addEventListener('visibilitychange', wrapperHandler);
  } else {
    return isAvailable('webview.onVisibilityChange').then(() => {
      const wrapperHandler = (
        result: NativeAPIResponseMessage<{ visibilityState: VisibilityState }>
      ) => {
        if (result.returnCode === 0) {
          visibilityChangeCallback(result.result.visibilityState);
        }
      };
      visibilityHandlerMap.set(visibilityChangeCallback, wrapperHandler);
      registerSpecificNotificationHandler(
        'UnifyNotif_OnWebviewVisibilityChange',
        wrapperHandler
      );
    });
  }
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.offVisibilityChange
 *
 * [PLATFORM]
 * mac        6.0.5+
 * windows    6.0.5+
 * linux      6.0.5+
 * ios        NotSupport
 * android    NotSupport
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export function offVisibilityChange(
  visibilityChangeCallback: (visibilityState: VisibilityState) => void
) {
  if (Platform.isMacOS() || Platform.isWindows()) {
    if (visibilityHandlerMap.has(visibilityChangeCallback)) {
      document.removeEventListener(
        'visibilitychange',
        visibilityHandlerMap.get(visibilityChangeCallback)
      );
      visibilityHandlerMap.delete(visibilityChangeCallback);
    }
  } else {
    return isAvailable('webview.onVisibilityChange').then(() => {
      if (visibilityHandlerMap.has(visibilityChangeCallback)) {
        unregisterSpecificNotificationHandler(
          'UnifyNotif_OnWebviewVisibilityChange',
          visibilityHandlerMap.get(visibilityChangeCallback)
        );
        visibilityHandlerMap.delete(visibilityChangeCallback);
      }
    });
  }
}

type VisibilityState = 'hidden' | 'visible';

export type sendMessage<T = any> = {
  data: T;
  webViewId: string;
};

const ReceiveMessageNotification = 'UnifyNotif_OnReceiveMessage';
/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.send
 *
 * [PLATFORM]
 * mac        6.2.5+
 * windows    6.2.5+
 * linux      6.2.5+
 * ios        6.2.5+
 * android    6.2.5+
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export function send<T = any>(
  data: sendMessage<T>,
  options?: PostMessageOption
) {
  return isAvailable('webview.send').then(() => {
    return postJsMessage(API.sendMessage, { ...data }, options);
  });
}

export type WebviewMessageData<T = any> = {
  data: T;
  webViewId: string;
  notificationId: string;
};

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.onReceiveMessage
 *
 * [PLATFORM]
 * mac        6.2.5+
 * windows    6.2.5+
 * linux      6.2.5+
 * ios        6.2.5+
 * android    6.2.5+
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export function onReceiveMessage<T = any>(
  onReceiveMessageCallback: NotificationHandler<WebviewMessageData<T>>
) {
  registerSpecificNotificationHandler(
    ReceiveMessageNotification,
    onReceiveMessageCallback
  );
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.offReceiveMessage
 *
 * [PLATFORM]
 * mac        6.2.5+
 * windows    6.2.5+
 * linux      6.2.5+
 * ios        6.2.5+
 * android    6.2.5+
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export function offReceiveMessage<T = any>(
  onReceiveMessageCallback: NotificationHandler<WebviewMessageData<T>>
) {
  unregisterSpecificNotificationHandler(
    ReceiveMessageNotification,
    onReceiveMessageCallback
  );
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.replyNotification
 *
 * [PLATFORM]
 * mac        6.2.5+
 * windows    6.2.5+
 * linux      6.2.5+
 * ios        6.2.5+
 * android    6.2.5+
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export function replyNotification(
  params: ReplyNotification
): Promise<NativeAPIResponseMessage<any>> {
  return isAvailable('webview.replyNotification').then(() => {
    return postJsMessage(API.replyNotification, { ...params });
  });
}

export type ReplyNotification = {
  notificationId: string;
  data: any;
};

const WebviewWillRecycleNotification = 'UnifyNotif_OnWebviewWillRecycle';

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.onWebviewWillRecycle
 *
 * [PLATFORM]
 * mac        6.4.0+
 * windows    6.4.0+
 * linux      6.4.0+
 * ios        NotSupport
 * android    NotSupport
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export function onWebviewWillRecycle<T = any>(
  onWebviewWillRecycleCallback: NotificationHandler<T>
) {
  registerSpecificNotificationHandler(
    WebviewWillRecycleNotification,
    onWebviewWillRecycleCallback
  );
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.offWebviewWillRecycle
 *
 * [PLATFORM]
 * mac        6.3.0+
 * windows    6.3.0+
 * linux      6.3.0+
 * ios        NotSupport
 * android    NotSupport
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export function offWebviewWillRecycle<T = any>(
  onWebviewWillRecycleCallback: NotificationHandler<T>
) {
  unregisterSpecificNotificationHandler(
    WebviewWillRecycleNotification,
    onWebviewWillRecycleCallback
  );
}

const WebviewLostFocusNotification = 'UnifyNotify_OnWebviewLostFocus';

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.onWebviewLostFocus
 *
 * [PLATFORM]
 * mac        6.3.0+
 * windows    6.3.0+
 * linux      6.3.0+
 * ios        NotSupport
 * android    NotSupport
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export function onWebviewLostFocus<T = any>(
  onWebviewLostFocusCallback: NotificationHandler<T>
) {
  registerSpecificNotificationHandler(
    WebviewLostFocusNotification,
    onWebviewLostFocusCallback
  );
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.offWebviewLostFocus
 *
 * [PLATFORM]
 * mac        6.3.0+
 * windows    6.3.0+
 * linux      6.3.0+
 * ios        NotSupport
 * android    NotSupport
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export function offWebviewLostFocus<T = any>(
  onWebviewLostFocusCallback: NotificationHandler<T>
) {
  unregisterSpecificNotificationHandler(
    WebviewLostFocusNotification,
    onWebviewLostFocusCallback
  );
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.saveWebContent
 *
 * [PLATFORM]
 * mac        6.3.0+
 * windows    6.3.0+
 * linux      6.3.0+
 * ios        NotSupport
 * android    NotSupport
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export function saveWebContent(
  params: WebviewData
): Promise<NativeAPIResponseMessage<any>> {
  return isAvailable('webview.saveWebContent').then(() => {
    return postJsMessage(API.saveWebContent, { ...params });
  });
}

export type WebviewData = {
  accept: boolean;
  data: string;
};

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.restoreWebContent
 *
 * [PLATFORM]
 * mac        6.3.0+
 * windows    6.3.0+
 * linux      6.3.0+
 * ios        NotSupport
 * android    NotSupport
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export function restoreWebContent(
  options?: PostMessageOption
): Promise<NativeAPIResponseMessage<{ data: string }>> {
  return isAvailable('webview.restoreWebContent').then(() => {
    return postJsMessage(
      API.restoreWebContent,
      {},
      { timeout: 60, ...options, needResponse: true }
    );
  });
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.resize
 *
 * [PLATFORM]
 * mac        6.3.0+
 * windows    6.3.0+
 * linux      6.3.0+
 * ios        notSupport
 * android    notSupport
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export function resize(rect: Rect, options?: PostMessageOption) {
  return isAvailable('webview.resize').then(() => {
    return postJsMessage(API.webviewResize, rect, options);
  });
}

export type Rect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type onPageEnterForegroundCallback = (data: {}) => void;
const PageEnterForegroundNotification = [
  'UnifyNotify_OnPageEnterForeground',
  'UnifyNotif_OnPageEnterForeground',
];

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.onPageEnterForeground
 *
 * [API]
 *
 * [PLATFORM]
 * mac        NotSupport
 * windows    NotSupport
 * linux      NotSupport
 * ios        6.4.0+
 * android    6.4.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export function onPageEnterForeground(
  onPageEnterForegroundCallback: onPageEnterForegroundCallback
) {
  registerSpecificNotificationHandler(
    PageEnterForegroundNotification,
    onPageEnterForegroundCallback
  );
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.offPageEnterForeground
 *
 * [PLATFORM]
 * mac        6.3.0+
 * windows    6.3.0+
 * linux      6.3.0+
 * ios        NotSupport
 * android    NotSupport
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export function offPageEnterForeground(
  onPageEnterForegroundCallback: onPageEnterForegroundCallback
) {
  unregisterSpecificNotificationHandler(
    PageEnterForegroundNotification,
    onPageEnterForegroundCallback
  );
}

export type onPageEnterBackgroundCallback = (data: {}) => void;
const PageEnterBackgroundNotification = [
  'UnifyNotify_OnPageEnterBackground',
  'UnifyNotif_OnPageEnterBackground',
];

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.onPageEnterBackground
 *
 * [PLATFORM]
 * mac        NotSupport
 * windows    NotSupport
 * linux      NotSupport
 * ios        6.4.0+
 * android    6.4.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export function onPageEnterBackground(
  onPageEnterBackgroundCallback: onPageEnterBackgroundCallback
) {
  registerSpecificNotificationHandler(
    PageEnterBackgroundNotification,
    onPageEnterBackgroundCallback
  );
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.offPageEnterBackground
 *
 * [PLATFORM]
 * mac        6.3.0+
 * windows    6.3.0+
 * linux      6.3.0+
 * ios        NotSupport
 * android    NotSupport
 *
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export function offPageEnterBackground(
  onPageEnterBackgroundCallback: onPageEnterBackgroundCallback
) {
  unregisterSpecificNotificationHandler(
    PageEnterBackgroundNotification,
    onPageEnterBackgroundCallback
  );
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.getPerformanceMetrics
 *
 * [PLATFORM]
 * mac        6.4.0+
 * windows    6.4.0+
 * linux      6.4.0+
 * ios        6.4.0+
 * android    6.4.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export function getPerformanceMetrics(
  options?: PostMessageOption
): Promise<NativeAPIResponseMessage<{ data: string }>> {
  return isAvailable('webview.getPerformanceMetrics').then(() => {
    return postJsMessage(
      API.getPerformanceMetrics,
      {},
      { timeout: 60, ...options, needResponse: true }
    );
  });
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.reload
 *
 * [PLATFORM]
 * mac        6.4.0
 * windows    NotSupport
 * linux      NotSupport
 * ios        NotSupport
 * android    NotSupport
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export function reload(): Promise<any> {
  return isAvailable('webview.reload').then(() => {
    return postJsMessage(API.reload, {});
  });
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.recreate
 *
 * [PLATFORM]
 * mac        NotSupport
 * windows    NotSupport
 * linux      NotSupport
 * ios        6.4.5
 * android    NotSupport
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export function recreate(homeUrl: string = ''): Promise<any> {
  return isAvailable('webview.recreate').then(() => {
    return postJsMessage(API.recreate, { homeUrl });
  });
}

export default {
  closeWebview,
  getWebviewId,
  isInWebview,
  onVisibilityChange,
  offVisibilityChange,
  send,
  resize,
  onReceiveMessage,
  offReceiveMessage,
  replyNotification,
  onWebviewWillRecycle,
  offWebviewWillRecycle,
  onWebviewLostFocus,
  offWebviewLostFocus,
  saveWebContent,
  restoreWebContent,
  onPageEnterBackground,
  offPageEnterBackground,
  onPageEnterForeground,
  offPageEnterForeground,
  getPerformanceMetrics,
  reload,
  recreate,
};

export type { VisibilityState };
